/**
 * Complete list of workflow functions available for use in expressions
 */
export const WORKFLOW_FUNCTIONS = [
  // String Functions
  {
    name: 'FX_ISNULLORWHITESPACE',
    signature: 'FX_ISNULLORWHITESPACE(value)',
    description: 'Returns true if the value is null, empty, or contains only whitespace',
    params: ['value'],
    category: 'String'
  },
  {
    name: 'FX_URLENCODE',
    signature: 'FX_URLENCODE(input)',
    description: 'URL encodes the input string',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_REPLACEXML',
    signature: 'FX_REPLACEXML(input)',
    description: 'Replaces XML special characters with entities (&, ", \', <, >)',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_CLEANXML',
    signature: 'FX_CLEANXML(input)',
    description: 'Alias for FX_REPLACEXML - replaces XML special characters with entities',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_UNCLEANXML',
    signature: 'FX_UNCLEANXML(input)',
    description: 'Converts XML entities back to special characters',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_CHAR',
    signature: 'FX_CHAR(input)',
    description: 'Converts UTF-16 code to character (e.g., FX_CHAR(65) returns "A")',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_CRLF',
    signature: 'FX_CRLF()',
    description: 'Returns carriage return + line feed characters',
    params: [],
    category: 'String'
  },
  {
    name: 'FX_TAB',
    signature: 'FX_TAB()',
    description: 'Returns tab character',
    params: [],
    category: 'String'
  },
  {
    name: 'FX_CLEAN',
    signature: 'FX_CLEAN(input)',
    description: 'Removes all non-printable characters from string',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_REMOVENEWLINE',
    signature: 'FX_REMOVENEWLINE(input)',
    description: 'Removes newline characters from string',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_FIND',
    signature: 'FX_FIND(input, search_string)',
    description: 'Returns position of search_string in input (1-based index, 0 if not found)',
    params: ['input', 'search_string'],
    category: 'String'
  },
  {
    name: 'FX_FINDREGEX',
    signature: 'FX_FINDREGEX(input, regex)',
    description: 'Returns position of first regex match in input (1-based index, 0 if not found)',
    params: ['input', 'regex'],
    category: 'String'
  },
  {
    name: 'FX_MATCHREGEX',
    signature: 'FX_MATCHREGEX(input, regex)',
    description: 'Returns the first match of regex in input',
    params: ['input', 'regex'],
    category: 'String'
  },
  {
    name: 'FX_REPLACE',
    signature: 'FX_REPLACE(input, oldValue, newValue)',
    description: 'Replaces all occurrences of oldValue with newValue',
    params: ['input', 'oldValue', 'newValue'],
    category: 'String'
  },
  {
    name: 'FX_REPLACEREGEX',
    signature: 'FX_REPLACEREGEX(stringToSearch, searchRegex, replacementString)',
    description: 'Replaces all regex matches with replacementString',
    params: ['stringToSearch', 'searchRegex', 'replacementString'],
    category: 'String'
  },
  {
    name: 'FX_UPPER',
    signature: 'FX_UPPER(input)',
    description: 'Converts string to uppercase',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_LOWER',
    signature: 'FX_LOWER(input)',
    description: 'Converts string to lowercase',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_CONCAT',
    signature: 'FX_CONCAT(arg1, arg2, ...)',
    description: 'Concatenates multiple strings',
    params: ['arg1', 'arg2', '...'],
    category: 'String'
  },
  {
    name: 'FX_CONCATENATE',
    signature: 'FX_CONCATENATE(arg1, arg2, ...)',
    description: 'Alias for FX_CONCAT - concatenates multiple strings',
    params: ['arg1', 'arg2', '...'],
    category: 'String'
  },
  {
    name: 'FX_LEFT',
    signature: 'FX_LEFT(input, count)',
    description: 'Returns leftmost count characters from input',
    params: ['input', 'count'],
    category: 'String'
  },
  {
    name: 'FX_RIGHT',
    signature: 'FX_RIGHT(input, count)',
    description: 'Returns rightmost count characters from input',
    params: ['input', 'count'],
    category: 'String'
  },
  {
    name: 'FX_MID',
    signature: 'FX_MID(input, start, count)',
    description: 'Returns count characters from input starting at position start (1-based)',
    params: ['input', 'start', 'count'],
    category: 'String'
  },
  {
    name: 'FX_LEN',
    signature: 'FX_LEN(input)',
    description: 'Returns the length of input string',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_PARSE',
    signature: 'FX_PARSE(input, tokenIndex, delimiter)',
    description: 'Splits input by delimiter and returns token at tokenIndex (0-based)',
    params: ['input', 'tokenIndex', 'delimiter'],
    category: 'String'
  },
  {
    name: 'FX_FORMAT',
    signature: 'FX_FORMAT(stringFormat, expression)',
    description: 'Formats expression using stringFormat (e.g., "N2" for numbers, "yyyy-MM-dd" for dates)',
    params: ['stringFormat', 'expression'],
    category: 'String'
  },
  {
    name: 'FX_PADL',
    signature: 'FX_PADL(input, width, pad_character)',
    description: 'Pads input on left to width with pad_character (default space)',
    params: ['input', 'width', 'pad_character'],
    category: 'String'
  },
  {
    name: 'FX_PADR',
    signature: 'FX_PADR(input, width, pad_character)',
    description: 'Pads input on right to width with pad_character (default space)',
    params: ['input', 'width', 'pad_character'],
    category: 'String'
  },
  {
    name: 'FX_TRIM',
    signature: 'FX_TRIM(input)',
    description: 'Removes whitespace from both ends of string',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_TRIMSTART',
    signature: 'FX_TRIMSTART(input)',
    description: 'Removes whitespace from start of string',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_TRIMEND',
    signature: 'FX_TRIMEND(input)',
    description: 'Removes whitespace from end of string',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_ESCAPESINGLEQUOTE',
    signature: 'FX_ESCAPESINGLEQUOTE(input)',
    description: 'Escapes single quotes by doubling them (useful for SQL)',
    params: ['input'],
    category: 'String'
  },
  {
    name: 'FX_ESCAPESOQL',
    signature: 'FX_ESCAPESOQL(value)',
    description: 'Escapes a value for safe use inside a single-quoted SOQL (Salesforce) string literal using backslash escaping: backslashes become \\\\ and single quotes become \\\'. Wrap any value placed inside quotes in a SOQL query, especially free-text fields like names that may contain apostrophes',
    params: ['value'],
    category: 'String'
  },
  {
    name: 'FX_STRIP',
    signature: 'FX_STRIP(string_expression, identifier)',
    description: 'Returns only specified character types: A=Alpha, N=Numeric, P=Punctuation, W=Whitespace, O=Other. Can combine (e.g., "AN" for alphanumeric)',
    params: ['string_expression', 'identifier'],
    category: 'String'
  },

  // Date Functions
  {
    name: 'FX_DATE',
    signature: 'FX_DATE(year, month, day)',
    description: 'Creates a date from year, month, and day',
    params: ['year', 'month', 'day'],
    category: 'Date'
  },
  {
    name: 'FX_STARTOFPRIORMONTH',
    signature: 'FX_STARTOFPRIORMONTH()',
    description: 'Returns the first day of the prior month',
    params: [],
    category: 'Date'
  },
  {
    name: 'FX_ENDOFPRIORMONTH',
    signature: 'FX_ENDOFPRIORMONTH()',
    description: 'Returns the last day of the prior month',
    params: [],
    category: 'Date'
  },
  {
    name: 'FX_STARTOFCURRENTMONTH',
    signature: 'FX_STARTOFCURRENTMONTH()',
    description: 'Returns the first day of the current month',
    params: [],
    category: 'Date'
  },
  {
    name: 'FX_ENDOFCURRENTMONTH',
    signature: 'FX_ENDOFCURRENTMONTH()',
    description: 'Returns the last day of the current month',
    params: [],
    category: 'Date'
  },
  {
    name: 'FX_DATEDIFF',
    signature: 'FX_DATEDIFF(interval, date1, date2)',
    description: 'Returns time difference between dates (yyyy, q, m, d, ww, h, n, s)',
    params: ['interval', 'date1', 'date2'],
    category: 'Date'
  },
  {
    name: 'FX_DATEPART',
    signature: 'FX_DATEPART(interval, date)',
    description: 'Extracts part of date (yyyy, m, d, dy, ww, h, n, s)',
    params: ['interval', 'date'],
    category: 'Date'
  },
  {
    name: 'FX_DATEADD',
    signature: 'FX_DATEADD(interval, number, date)',
    description: 'Adds time to date (yyyy, q, m, d, ww, h, n, s)',
    params: ['interval', 'number', 'date'],
    category: 'Date'
  },
  {
    name: 'FX_DATETIME',
    signature: 'FX_DATETIME(year, month, day, hour, minute, second)',
    description: 'Creates a DateTime with time components',
    params: ['year', 'month', 'day', 'hour', 'minute', 'second'],
    category: 'Date'
  },
  {
    name: 'FX_GETDATETIME',
    signature: 'FX_GETDATETIME()',
    description: 'Returns current date and time',
    params: [],
    category: 'Date'
  },
  {
    name: 'FX_GETUTCDATETIME',
    signature: 'FX_GETUTCDATETIME()',
    description: 'Returns current UTC date and time',
    params: [],
    category: 'Date'
  },
  {
    name: 'FX_ISDATE',
    signature: 'FX_ISDATE(input)',
    description: 'Returns true if input is a valid date',
    params: ['input'],
    category: 'Date'
  },

  // Math Functions
  {
    name: 'FX_ABS',
    signature: 'FX_ABS(input)',
    description: 'Returns the absolute value of a number',
    params: ['input'],
    category: 'Math'
  },
  {
    name: 'FX_MULTIPLY',
    signature: 'FX_MULTIPLY(value1, value2)',
    description: 'Multiplies two decimal values',
    params: ['value1', 'value2'],
    category: 'Math'
  },
  {
    name: 'FX_DIVIDE',
    signature: 'FX_DIVIDE(value1, value2)',
    description: 'Divides value1 by value2',
    params: ['value1', 'value2'],
    category: 'Math'
  },
  {
    name: 'FX_MOD',
    signature: 'FX_MOD(value1, value2)',
    description: 'Returns the remainder of dividing value1 by value2',
    params: ['value1', 'value2'],
    category: 'Math'
  },
  {
    name: 'FX_SUBTRACT',
    signature: 'FX_SUBTRACT(value1, value2)',
    description: 'Subtracts value2 from value1',
    params: ['value1', 'value2'],
    category: 'Math'
  },
  {
    name: 'FX_SUM',
    signature: 'FX_SUM(value1, value2, ...)',
    description: 'Sums multiple numeric values',
    params: ['value1', 'value2', '...'],
    category: 'Math'
  },
  {
    name: 'FX_ROUND',
    signature: 'FX_ROUND(input, decimals)',
    description: 'Rounds a number to the specified number of decimal places',
    params: ['input', 'decimals'],
    category: 'Math'
  },

  // Conversion Functions
  {
    name: 'FX_TOBOOLEAN',
    signature: 'FX_TOBOOLEAN(input)',
    description: 'Converts input to boolean (supports true/false, yes/no, 1/0)',
    params: ['input'],
    category: 'Conversion'
  },
  {
    name: 'FX_TODECIMAL',
    signature: 'FX_TODECIMAL(input)',
    description: 'Converts input to decimal number',
    params: ['input'],
    category: 'Conversion'
  },
  {
    name: 'FX_TODOUBLE',
    signature: 'FX_TODOUBLE(input)',
    description: 'Converts input to double number',
    params: ['input'],
    category: 'Conversion'
  },
  {
    name: 'FX_TOINTEGER',
    signature: 'FX_TOINTEGER(input)',
    description: 'Converts input to integer',
    params: ['input'],
    category: 'Conversion'
  },
  {
    name: 'FX_TOSTRING',
    signature: 'FX_TOSTRING(input)',
    description: 'Converts input to string',
    params: ['input'],
    category: 'Conversion'
  },
  {
    name: 'FX_TOBASE64',
    signature: 'FX_TOBASE64(input)',
    description: 'Converts input to Base64 encoded string',
    params: ['input'],
    category: 'Conversion'
  },
  {
    name: 'FX_HMACSHA256',
    signature: 'FX_HMACSHA256(input, secretKey, outputFormat)',
    description: 'Computes an HMAC SHA-256 hash of the input string using the provided secret key. outputFormat can be "base64" or "hex" (default: "hex")',
    params: ['input', 'secretKey', 'outputFormat'],
    category: 'Miscellaneous'
  },

  // Logical Functions
  {
    name: 'FX_AND',
    signature: 'FX_AND(condition1, condition2, ...)',
    description: 'Returns true if all conditions are true',
    params: ['condition1', 'condition2', '...'],
    category: 'Logical'
  },
  {
    name: 'FX_OR',
    signature: 'FX_OR(condition1, condition2, ...)',
    description: 'Returns true if any condition is true',
    params: ['condition1', 'condition2', '...'],
    category: 'Logical'
  },
  {
    name: 'FX_NOT',
    signature: 'FX_NOT(condition)',
    description: 'Returns the opposite boolean value',
    params: ['condition'],
    category: 'Logical'
  },
  {
    name: 'FX_IF',
    signature: 'FX_IF(condition, trueValue, falseValue)',
    description: 'Returns trueValue if condition is true, otherwise falseValue',
    params: ['condition', 'trueValue', 'falseValue'],
    category: 'Logical'
  },
  {
    name: 'FX_IFNULLUSE',
    signature: 'FX_IFNULLUSE(input, replacement)',
    description: 'Returns input if not null, otherwise returns replacement',
    params: ['input', 'replacement'],
    category: 'Logical'
  },
  {
    name: 'FX_IFNULLOREMPTYUSE',
    signature: 'FX_IFNULLOREMPTYUSE(input, replacement)',
    description: 'Returns input if not null or empty, otherwise returns replacement',
    params: ['input', 'replacement'],
    category: 'Logical'
  },
  {
    name: 'FX_ISNULL',
    signature: 'FX_ISNULL(input)',
    description: 'Returns true if input is null',
    params: ['input'],
    category: 'Logical'
  },
  {
    name: 'FX_ISNULLOREMPTY',
    signature: 'FX_ISNULLOREMPTY(input)',
    description: 'Returns true if input is null or empty (after trimming)',
    params: ['input'],
    category: 'Logical'
  },

  // Lookup Functions
  {
    name: 'FX_LOOKUPVALUEBYKEY',
    signature: 'FX_LOOKUPVALUEBYKEY(lookup, key)',
    description: 'Searches lookup object for matching key and returns its value',
    params: ['lookup', 'key'],
    category: 'Lookup'
  },
  {
    name: 'FX_LOOKUPVALUEBYKEYDEFAULT',
    signature: 'FX_LOOKUPVALUEBYKEYDEFAULT(lookup, key, defaultValue)',
    description: 'Searches lookup for key and returns value, or defaultValue if not found',
    params: ['lookup', 'key', 'defaultValue'],
    category: 'Lookup'
  },
  {
    name: 'FX_LOOKUPVALUEBYVALUE',
    signature: 'FX_LOOKUPVALUEBYVALUE(lookup, value)',
    description: 'Searches lookup object for matching value and returns its key',
    params: ['lookup', 'value'],
    category: 'Lookup'
  },
  {
    name: 'FX_LOOKUPVALUEBYVALUEDEFAULT',
    signature: 'FX_LOOKUPVALUEBYVALUEDEFAULT(lookup, value, defaultValue)',
    description: 'Searches lookup for value and returns key, or defaultValue if not found',
    params: ['lookup', 'value', 'defaultValue'],
    category: 'Lookup'
  },

  // Miscellaneous Functions
  {
    name: 'FX_NEWGUID',
    signature: 'FX_NEWGUID()',
    description: 'Generates a new globally unique identifier (GUID)',
    params: [],
    category: 'Miscellaneous'
  }
];
